// @noflow
import React from "react";

import {
    dateFromDayShift,
    deepClone,
    fromShiftTag,
    niceDate,
    SHIFT_CONSTS,
    shiftNumber,
    shiftTag,
    TIME_RANGES,
    weekToDayShift,
    toISODateString,
    isoDateFromShiftTag,
} from "../../../lib/Util";

import { getAllLinesForLineGroup } from "../../../lib/BusinessLogic";
import { getBackend } from "../../../lib/backend/Backend2";
import { translate } from "../../IntlProviderWrapper";
import ErrorComponent from "../../ErrorComponent";
import Loader from "../../Loader";
import * as Auth from "../../../lib/Auth";
import { PreplanningChartModal } from "./PreplanningChart";

import type {
    CalendarLineOption,
    ProductionUntilTodayOverall,
    ShiftNumberData,
    UntilTodayOverall,
} from "../../../lib/Models";
import { SHIFT_MODIFIERS } from "../../../lib/Models";
import type {
    ICompetenceData,
    IGetPeopleShiftsTableRes,
    ILineData,
    IPreplanningDataExpectedProduction,
    IPreplanningDataLine,
    IPreplanningDataProduction,
    IPreplanningOverrideDataLine,
    IHoliday,
    IShiftLineRec,
    IShiftPersonLineRec,
    IShiftPersonLineStoreRec,
    IUpdateLineShifts,
} from "../../../lib/backend/manufacturing2.generated.types";
import PreplanningEditLineModal from "./PreplanningEditLineModal";

import { LINE_TAGS_ACCESS, LINE_TYPES } from "../../../lib/ManufacturingTags.generated";
import PreplanningProductGroupSumModal from "./PreplanningProductGroupSumModal";
import PreplanningTable from "./PreplanningTable";
import ConfirmationModal from "../../ConfirmationModal";

import type { ConfirmationModalProps } from "../../ConfirmationModal";
import type {
    AvailablePeopleMap,
    CompetenceSumUp,
    CompetenceTitleFactorMap,
    CompetenceSumUpInput,
    ShiftCompetencesMap,
} from "./PreplanningTable";

import "./Preplanning.scss";

type LineShiftsMap = Map<string, IShiftLineRec[]>;
type TimeShiftsMap = Map<number, IShiftLineRec[]>;

type Competence = {
    id: number;
    title: string;
    factor: number;
};

type LineCompetencesMap = Map<string, Competence[]>; // line_uuid -> CompetenceFactor[]

type Props = {
    plant_uuid: string;
    line_group_uuid: string;
};

type State = {
    date: Date;
    error: string;
    expected_productions: IPreplanningDataExpectedProduction[];
    is_calculating: boolean;
    is_loading: boolean;
    is_saving: boolean;
    lines: ILineData[];
    child_lines: ILineData[];
    preplanning_lines: IPreplanningDataLine[];
    shifts: IShiftLineRec[];
    shifts_map: Map<string, IShiftLineRec>;
    calendar_line_options: CalendarLineOption[];
    calendar_line: string;
    edit_line_modal_show: boolean;
    editing_line_uuid: string;
    line_competences_map: LineCompetencesMap;
    line_product_group: Map<string, string>;
    map_available_people_per_shift: AvailablePeopleMap;
    map_available_people_per_day: AvailablePeopleMap;
    availability_line_uuid: string;
    show_preplanning_chart_modal: boolean;
    product_group_line_map: Map<string, string[]>;
    product_group_sum_map: Map<string, ProductionUntilTodayOverall>;
    sum_per_line: Map<string, ProductionUntilTodayOverall>;
    production_group_sum_modal_show: boolean;
    production_status_period: boolean;
    public_holidays: IHoliday[];
    show_save_confirmation_modal: boolean;
    dirty_people_schedule: IShiftPersonLineRec[];
    people_schedule_updates: IShiftPersonLineStoreRec[];
    people_schedule_hash: string;
};

const calendar_line_options_filename = "calendar_line_options.json";

const getFirstShift = (date: Date): ShiftNumberData => {
    const start_date = new Date(date.getFullYear(), date.getMonth(), 1);
    const start_shift_date = dateFromDayShift(start_date, 0);
    return shiftNumber(start_shift_date);
};

const getLastShift = (date: Date): ShiftNumberData => {
    const end_date = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const end_shift_date = dateFromDayShift(end_date, SHIFT_CONSTS.SHIFT_START_HOURS.length - 1);
    return shiftNumber(end_shift_date);
};

const getMonthDate = (date: Date): Date => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
};

const getShiftKey = (shift: IShiftLineRec): string => {
    return `${shift.line_uuid}_${shift.shift_uuid}`;
};

const getShiftLabel = (day_shift: number): string => {
    const shift_names = [
        translate("common.morning_shift", "Early"),
        translate("common.afternoon_shift", "Late"),
        translate("common.night_shift", "Night"),
    ];
    let shift_label = `${day_shift + 1}.`;
    if (day_shift < shift_names.length) {
        shift_label += ` ${shift_names[day_shift]}`;
    }

    return shift_label;
};

const getShiftBounds = (shift_tags: string[]): [ShiftNumberData, ShiftNumberData] | null => {
    if (shift_tags.length === 0) {
        return null;
    }

    let from_shift_tag = shift_tags[0];
    let to_shift_tag = shift_tags[0];
    for (const shift_tag of shift_tags) {
        if (shift_tag < from_shift_tag) {
            from_shift_tag = shift_tag;
        }

        if (shift_tag > to_shift_tag) {
            to_shift_tag = shift_tag;
        }
    }

    const from_shift_number = shiftNumber(fromShiftTag(from_shift_tag));
    let to_shift_number = shiftNumber(fromShiftTag(to_shift_tag));

    // Increment the upper bound by one
    const end_shift_ts = to_shift_number.ts_start + SHIFT_CONSTS.SHIFT_DURATION_HOURS * TIME_RANGES.HOUR;
    to_shift_number = shiftNumber(end_shift_ts);

    return [from_shift_number, to_shift_number];
};

export const mapShiftsToLines = (shifts: IShiftLineRec[]): LineShiftsMap => {
    const line_shifts_map: LineShiftsMap = new Map();
    for (const shift of shifts) {
        const { line_uuid } = shift;
        let line_shifts = line_shifts_map.get(line_uuid);
        if (line_shifts === undefined) {
            line_shifts = [];
            line_shifts_map.set(line_uuid, line_shifts);
        }

        line_shifts.push(shift);
    }

    return line_shifts_map;
};

export const mapShiftsToTime = (shifts: IShiftLineRec[]): TimeShiftsMap => {
    const time_shifts_map: TimeShiftsMap = new Map();
    for (const shift of shifts) {
        const { shift_date } = shift;
        let time_shifts = time_shifts_map.get(shift_date);
        if (time_shifts === undefined) {
            time_shifts = [];
            time_shifts_map.set(shift_date, time_shifts);
        }

        time_shifts.push(shift);
    }

    return time_shifts_map;
};

/**
 * Maps child lines to their corresponding parent lines. Child lines without a parent ID or with a
 * parent ID that does not match any parent line are ignored.
 * Only one level of child lines is supported, meaning that child lines cannot have their own
 * children.
 *
 * @param {ILineData[]} parent_lines - Array of parent line data objects.
 * @param {ILineData[]} child_lines - Array of child line data objects.
 * @returns {Map<string, ILineData>} A map where each key is a child line UUID and the value is the
 *  corresponding parent line data object.
 */
const mapChildLinesToParentLine = (parent_lines: ILineData[], child_lines: ILineData[]): Map<string, ILineData> => {
    const parent_id_to_line_map: Map<number, ILineData> = new Map();
    for (const line of parent_lines) {
        parent_id_to_line_map.set(line.id, line);
    }

    const child_uuid_to_parent_line_map: Map<string, ILineData> = new Map();
    for (const line of child_lines) {
        const parent_line = line.parent_id !== null ? parent_id_to_line_map.get(line.parent_id) : undefined;
        if (parent_line !== undefined) {
            child_uuid_to_parent_line_map.set(line.uuid, parent_line);
        }
    }

    return child_uuid_to_parent_line_map;
};

/**
 * Maps parent lines (external IDs) to their corresponding competence factors.
 * @param {ILineData[]} parent_lines - Array of parent line data objects.
 * @param {ILineData[]} child_lines - Array of child line data objects.
 * @param {ICompetenceData[]} competences - Array of competence data objects.
 * @returns {LineCompetencesMap} A map where each key is a parent line external ID and the value
 * is an array of competences.
 */
const mapParentLinesToCompetences = (
    parent_lines: ILineData[],
    child_lines: ILineData[],
    competences: ICompetenceData[]
): LineCompetencesMap => {
    const line_id_to_competence_factors_map: LineCompetencesMap = new Map();
    const parent_id_to_external_id_map: Map<number, string> = new Map(
        parent_lines.map(line => [line.id, line.external_id])
    );
    const competence_id_to_title_map: Map<number, string> = new Map(
        competences.map(competence => [competence.id, competence.title])
    );

    for (const line of child_lines) {
        const { parent_id, competence_id, factor } = line;
        if (parent_id === null || competence_id === null) {
            continue;
        }

        const parent_line_external_id = parent_id_to_external_id_map.get(parent_id);
        const competence_title = competence_id_to_title_map.get(competence_id);
        if (parent_line_external_id === undefined || competence_title === undefined) {
            continue;
        }

        const competences = line_id_to_competence_factors_map.get(parent_line_external_id) || [];
        competences.push({
            id: competence_id,
            title: competence_title,
            factor: factor,
        });
        line_id_to_competence_factors_map.set(parent_line_external_id, competences);
    }

    return line_id_to_competence_factors_map;
};

export const formatDate = (date: Date) => {
    let year = date.getFullYear();
    let month = (1 + date.getMonth()).toString().padStart(2, "0");
    let day = date.getDate().toString().padStart(2, "0");

    return `${year}-${month}-${day}`;
};

const MonthPicker = (props: { date: Date; disabled?: boolean; onChange: (date: Date) => void }) => {
    const { date, disabled = false, onChange } = props;
    const now = new Date();

    // Calculate six months ago
    const six_months_ago = new Date(now.getFullYear(), now.getMonth() - 6, 1);
    const min_month_date = getMonthDate(six_months_ago);

    // Calculate six months in the future
    const six_months_later = new Date(now.getFullYear(), now.getMonth() + 6, 1);
    const max_month_date = getMonthDate(six_months_later);

    // Get the current month date
    const current_month_date = getMonthDate(date);

    const handleButtonClick = (diff: number) => {
        const new_month_date = getMonthDate(date);
        new_month_date.setMonth(new_month_date.getMonth() + diff);
        if (new_month_date < min_month_date || new_month_date > max_month_date) {
            return;
        }

        onChange(new_month_date);
    };

    return (
        <span className="preplanning__month-picker week-picker">
            <button
                className="btn btn-light btn-short"
                disabled={disabled || current_month_date.toISOString() === min_month_date.toISOString()}
                onClick={() => handleButtonClick(-1)}
            >
                &lt;
            </button>
            <span>
                <strong className="vertical-align-middle">
                    {translate(`common.month${date.getMonth()}`).slice(0, 3)}
                </strong>{" "}
                <span className="vertical-align-middle">{date.getFullYear()}</span>
            </span>
            <button
                className="btn btn-light btn-short"
                disabled={disabled || current_month_date.toISOString() === max_month_date.toISOString()}
                onClick={() => handleButtonClick(+1)}
            >
                &gt;
            </button>
        </span>
    );
};

const SaveConfirmationModal = (
    props: ConfirmationModalProps & {
        schedule: IShiftPersonLineRec[];
    }
) => {
    const { schedule, ...modal_props } = props;
    return (
        <ConfirmationModal
            {...modal_props}
            class_name="modal-dialog-scrollable"
            title={translate("common.save_changes", "Save changes")}
            accept_label={translate("common.save", "Save")}
            cancel_label={translate("common.cancel", "Cancel")}
        >
            <div className="preplanning__save-confirmation">
                <p>
                    {translate(
                        "Preplanning.save_closed_shifts_warning",
                        "Some lines have people assigned to the shifts that will be disabled. People will be automatically removed from disabled shifts."
                    )}
                </p>
                <details>
                    <summary>{translate("common.details", "Details")}</summary>
                    <table className="table table-bordered table-hover">
                        <thead>
                            <tr>
                                <th>{translate("common.line", "Line")}</th>
                                <th>{translate("common.date", "Date")}</th>
                                <th>{translate("common.shift", "Shift")}</th>
                                <th>{translate("common.people", "People")}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {schedule.map((line, index) =>
                                line.shifts.map((shift, shift_index) => {
                                    const date = fromShiftTag(shift.shift_tag);
                                    const day_shift = weekToDayShift(shift.shift_number);
                                    const shift_label = getShiftLabel(day_shift);
                                    if (shift_index > 0) {
                                        return (
                                            <tr key={`${index}-${shift_index}`}>
                                                <td>{niceDate(date)}</td>
                                                <td>{shift_label}</td>
                                                <td>
                                                    {shift.persons.map((person, i) => (
                                                        <div key={i}>{person.name}</div>
                                                    ))}
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return (
                                        <tr key={`${index}-${shift_index}`}>
                                            <th rowSpan={line.shifts.length}>{line.line_title}</th>
                                            <td>{niceDate(date)}</td>
                                            <td>{shift_label}</td>
                                            <td>
                                                {shift.persons.map((person, i) => (
                                                    <div key={i}>{person.name}</div>
                                                ))}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </details>
            </div>
        </ConfirmationModal>
    );
};

class Preplanning extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const state: State = {
            date: getMonthDate(new Date()),
            error: "",
            expected_productions: [],
            is_calculating: false,
            is_loading: false,
            is_saving: false,
            lines: [],
            child_lines: [],
            preplanning_lines: [],
            shifts: [],
            shifts_map: new Map(),
            calendar_line_options: [],
            calendar_line: "",
            edit_line_modal_show: false,
            editing_line_uuid: "",
            line_competences_map: new Map(),
            line_product_group: new Map(),
            map_available_people_per_shift: new Map(),
            map_available_people_per_day: new Map(),
            availability_line_uuid: "",
            show_preplanning_chart_modal: false,
            product_group_line_map: new Map(),
            product_group_sum_map: new Map(),
            sum_per_line: new Map(),
            production_group_sum_modal_show: false,
            production_status_period: true,
            public_holidays: [],
            show_save_confirmation_modal: false,
            dirty_people_schedule: [],
            people_schedule_updates: [],
            people_schedule_hash: "",
        };
        this.state = state;
    }

    async componentDidMount() {
        await this.loadData();
    }

    async componentDidUpdate(prev_props: Props) {
        if (
            prev_props.plant_uuid !== this.props.plant_uuid ||
            prev_props.line_group_uuid !== this.props.line_group_uuid
            // prev_state.preplanning_lines !== this.state.preplanning_lines
        ) {
            await this.loadData();
        }
    }

    loadData = async (preserve_linegroup: boolean = false, show_loader: boolean = true) => {
        const { line_group_uuid } = this.props;
        const { date } = this.state;

        this.setState({ is_loading: show_loader, error: "" });

        try {
            let calendar_line_options = this.state.calendar_line_options;
            if (calendar_line_options.length === 0) {
                const { calendar_line_options: new_calendar_line_options } = await this.getCalendarLineOptionsFile();
                calendar_line_options = new_calendar_line_options;
            }

            const line_product_group_result = await getBackend().manufacturing.getPreplanningLinesProductGroups({});
            const line_product_group = new Map<string, string>(line_product_group_result.line_product_group);
            const product_group_line_map = new Map<string, string[]>(line_product_group_result.product_group_lines);

            const firstDay = new Date(this.state.date.getFullYear(), this.state.date.getMonth(), 1);
            const lastDay = new Date(this.state.date.getFullYear(), this.state.date.getMonth() + 1, 0);
            const public_holidays_result = await getBackend().manufacturing.getHolidays({
                start_date: toISODateString(firstDay),
                end_date: toISODateString(lastDay),
            });
            const public_holidays = public_holidays_result.holidays;

            const { productions: expected_productions } =
                await getBackend().manufacturing.getPreplanningExpectedProductions({
                    month: date.getMonth() + 1,
                    year: date.getFullYear(),
                });

            let { lines, child_lines, availability_line_uuid } = this.state;
            if (!preserve_linegroup) {
                const { lines: linegroup_lines } = await getAllLinesForLineGroup(this.props.line_group_uuid);

                lines = [];
                child_lines = [];
                availability_line_uuid = "";
                for (const line of linegroup_lines) {
                    const line_type = LINE_TAGS_ACCESS.type(line.tags);

                    if (line_type === LINE_TYPES.ordinary) {
                        // On preplanning ordinary production lines are displayed. Techinicaly
                        // ordinary lines can have child lines, and they are displayed.
                        lines.push(line);
                    } else if (line_type === LINE_TYPES.pool) {
                        // Pool lines are used for people availability. We assume that there is only
                        // one pool line per line group. We use the first pool line found.
                        if (availability_line_uuid === "") {
                            availability_line_uuid = line.uuid;
                        }
                    } else if (line.parent_id !== null) {
                        // Other child lines are not displayed on preplanning. They can be used
                        // for workforce scheduling and are relevant for e.g. calculating needed
                        // competences or for disabling/clearing shifts when parent line is disabled.
                        child_lines.push(line);
                    }
                }

                // Filter child lines to only those that have a parent line in the current set
                const child_line_uuid_to_parent_line_map = mapChildLinesToParentLine(lines, child_lines);
                child_lines = child_lines.filter(line => {
                    const parent_line = child_line_uuid_to_parent_line_map.get(line.uuid);
                    return parent_line !== undefined;
                });
            }

            const first_shift = getFirstShift(date);
            const last_shift = getLastShift(date);
            const { shifts } = await getBackend().manufacturing.getLineShiftsEx({
                line_uuids: lines.map(line => line.uuid),
                min_shift_tag: first_shift.shift_tag,
                max_shift_tag: last_shift.shift_tag,
            });
            shifts.sort((a, b) => a.shift_date - b.shift_date || a.shift_number - b.shift_number);

            const { lines: preplanning_lines } = await getBackend().manufacturing.getPreplanningData({
                line_group_uuid,
                month: date.getMonth(),
                year: date.getFullYear(),
            });

            // this is a hack to get the next to last shift (condition in API is > and not >=, thats why i need to add one shift)
            let next_to_last_shift = "";
            const last_shift_number = Number(last_shift.shift_tag.substr(6, 2)) + 1;

            next_to_last_shift = last_shift.shift_tag.substr(0, 6) + String(last_shift_number);

            const { competences } = await getBackend().manufacturing.getCompetences({
                ids: [...new Set(child_lines.map(line => line.competence_id ?? -1))].filter(id => id !== -1),
            });

            const line_competences_map = mapParentLinesToCompetences(lines, child_lines, competences);

            const people_available_result = await getBackend().manufacturing.getPreplanningPeopleAvailability({
                first_shift_tag: first_shift.shift_tag,
                last_shift_tag: next_to_last_shift,
                availability_line_uuid,
            });
            const map_available_people_per_shift: AvailablePeopleMap = new Map(
                people_available_result.available_people_per_shift
            );
            const map_available_people_per_day: AvailablePeopleMap = new Map(
                people_available_result.available_people_per_day
            );

            this.calculateNewSums(shifts, lines, preplanning_lines, line_product_group, product_group_line_map);
            this.setState({
                expected_productions: expected_productions.map(data => ({
                    material_group: data.material_group,
                    quantity: data.quantity,
                })),
                lines,
                child_lines,
                preplanning_lines,
                shifts: deepClone(shifts),
                shifts_map: new Map(shifts.map(s => [getShiftKey(s), s])),
                line_competences_map,
                map_available_people_per_shift,
                map_available_people_per_day,
                availability_line_uuid,
                line_product_group,
                product_group_line_map,
                calendar_line_options,
                public_holidays,
            });
        } catch (error: unknown) {
            this.setState({
                error: translate("common.error", "An error occurred while fetching data"),
            });
            console.error(error);
        }

        this.setState({ is_loading: false });
    };

    calculateNewSums = (
        shifts: IShiftLineRec[],
        lines: ILineData[],
        preplanning_lines: IPreplanningDataLine[],
        line_product_group: Map<string, string>,
        product_group_line_map: Map<string, string[]>
    ) => {
        // Initialize a Map to store the sum of produced and planned items per line
        const sum_per_line: Map<string, ProductionUntilTodayOverall> = new Map();
        const today_shift_number = shiftNumber(dateFromDayShift(new Date(), 0));
        const today_shift_tag = today_shift_number.shift_tag;

        const line_shifts_map = mapShiftsToLines(shifts);

        // Helper function to update a map with an item's quantity
        const updateSumMap = (map: Map<string, UntilTodayOverall>, item: IPreplanningDataProduction) => {
            const entry = map.get(item.shift_tag) || { until_today: 0, overall: 0 };
            entry.overall += item.quantity;
            if (item.shift_tag < today_shift_tag) {
                entry.until_today += item.quantity;
            }
            map.set(item.shift_tag, entry);
        };

        // Iterate over each line
        for (const line of lines) {
            const line_shifts = line_shifts_map.get(line.uuid);
            if (line_shifts === undefined) {
                return null;
            }

            const line_external_id = line.external_id;
            sum_per_line.set(line_external_id, {
                produced: { until_today: 0, overall: 0 },
                planned: { until_today: 0, overall: 0 },
            });

            const line_data = preplanning_lines.find(l => l.line_external_id === line_external_id);
            if (line_data === undefined) {
                continue;
            }

            // Initialize maps to store the quantity per shift
            // Until today includes all closed shifts without today's shifts
            const planned_map: Map<string, UntilTodayOverall> = new Map();
            const produced_map: Map<string, UntilTodayOverall> = new Map();

            line_data.materials.forEach(material => {
                material.planned.forEach(item => updateSumMap(planned_map, item));
                material.produced.forEach(item => updateSumMap(produced_map, item));
            });

            let produced_sum_until_today = 0;
            let planned_sum_until_today = 0;
            let produced_sum_overall = 0;
            let planned_sum_overall = 0;

            // Map the shifts to time buckets and iterate over each shift
            const time_shifts_map = mapShiftsToTime(line_shifts);
            for (const shifts of time_shifts_map.values()) {
                for (const shift of shifts) {
                    const shift_tag = shiftTag(shift.year, shift.week, shift.shift_number);

                    const planned = planned_map.get(shift_tag);
                    const produced = produced_map.get(shift_tag);
                    // For every past shift, count the quantity until today
                    produced_sum_until_today += produced ? produced.until_today : 0;
                    planned_sum_until_today += produced ? produced.until_today : 0;
                    if (shift_tag < today_shift_tag) {
                        // Count everything made until today - Bosch definition
                        planned_sum_overall += produced ? produced.until_today : 0;
                    } else {
                        // Count everything planned until the end of month
                        planned_sum_overall += planned ? planned.overall : 0;
                    }

                    produced_sum_overall += produced ? produced.overall : 0;
                }
            }

            const current_line = sum_per_line.get(line_external_id);
            if (current_line) {
                current_line.produced.until_today = produced_sum_until_today;
                current_line.planned.until_today = planned_sum_until_today;
                current_line.produced.overall = produced_sum_overall;
                current_line.planned.overall = planned_sum_overall;
            }
        }

        // Aggregate sums per product group
        const product_group_sum_map: Map<string, ProductionUntilTodayOverall> = new Map();

        for (const [key, value] of sum_per_line) {
            const { produced, planned } = value;
            const product_group_name = line_product_group.get(key.replace("058", ""));
            if (product_group_name === undefined) {
                continue;
            }
            const product_group_lines = product_group_line_map.get(product_group_name);
            if (product_group_lines === undefined || product_group_lines.length === 0) {
                continue;
            }
            const current_product_group_sum = product_group_sum_map.get(product_group_name);
            if (current_product_group_sum === undefined) {
                product_group_sum_map.set(product_group_name, {
                    produced: { ...produced },
                    planned: { ...planned },
                });
            } else {
                current_product_group_sum.produced.until_today += produced.until_today;
                current_product_group_sum.planned.until_today += planned.until_today;
                current_product_group_sum.produced.overall += produced.overall;
                current_product_group_sum.planned.overall += planned.overall;
            }
        }

        this.setState({ product_group_sum_map, sum_per_line, line_product_group });
    };

    getCalendarLineOptionsFile = async (): Promise<{ calendar_line_options: CalendarLineOption[] }> => {
        if (!Auth.roleCanEditPreplanning()) {
            return { calendar_line_options: [] };
        }

        try {
            const blob = await getBackend().common.getBlobStorageItemAsStringOrNull({
                key: calendar_line_options_filename,
            });
            this.setState({ error: "" });

            if (!blob.content) {
                return { calendar_line_options: [] };
            }

            let parsed;
            try {
                parsed = JSON.parse(blob.content);
            } catch (e) {
                return { calendar_line_options: [] };
            }

            return { calendar_line_options: parsed };
        } catch (error: unknown) {
            this.setState({
                error: translate("common.error", "An error occurred while fetching data"),
            });
            console.error(error);
            return { calendar_line_options: [] };
        }
    };

    getCompetences = (line_external_ids: string[]): CompetenceTitleFactorMap => {
        const { line_competences_map } = this.state;

        const all_needed_competences: CompetenceTitleFactorMap = new Map();

        for (const line_external_id of line_external_ids) {
            const line_competences = line_competences_map.get(line_external_id);
            if (line_competences !== undefined) {
                for (const line_competence of line_competences) {
                    const competence_title = line_competence.title;
                    const competence_factor = line_competence.factor;

                    let competence_factor_sum = all_needed_competences.get(competence_title) ?? 0;
                    all_needed_competences.set(competence_title, competence_factor_sum + competence_factor);
                }
            }
        }

        return all_needed_competences;
    };

    getCompetencesSumUpPerDay = (input_data: CompetenceSumUpInput, short_names: boolean = false): CompetenceSumUp => {
        if (short_names) {
            const all_short_needed_competences = this.getShortCompetencesSumUp(input_data);
            return all_short_needed_competences;
        } else {
            const all_needed_competences = this.getLongCompetencesSumUpPerDay(input_data);
            return all_needed_competences;
        }
    };

    getShortCompetencesSumUp = (input_data: CompetenceSumUpInput) => {
        // Get the long version of competences for days and shifts
        const { all_needed_competences_by_day, all_needed_competences_by_shift } =
            this.getLongCompetencesSumUpPerDay(input_data);

        // Helper: transform a competence map by shortening keys and rounding values
        const transform_competence_map = (competences: Map<string, number>): Map<string, number> => {
            const transformed = new Map<string, number>();
            for (const [competence, value] of competences) {
                const shortened_title = this.shortenCompetenceTitle(competence);
                // Round the value to two decimals
                transformed.set(shortened_title, Number(value.toFixed(2)));
            }
            return transformed;
        };

        // Create new maps with the shortened competences for days and shifts
        const short_competences_by_day: ShiftCompetencesMap = new Map();
        for (const [day, competences] of all_needed_competences_by_day) {
            short_competences_by_day.set(day, transform_competence_map(competences));
        }

        const short_competences_by_shift: ShiftCompetencesMap = new Map();
        for (const [shift, competences] of all_needed_competences_by_shift) {
            short_competences_by_shift.set(shift, transform_competence_map(competences));
        }

        return {
            all_needed_competences_by_day: short_competences_by_day,
            all_needed_competences_by_shift: short_competences_by_shift,
        };
    };

    // This function shortens a competence title by only showing what is inside parentheses
    shortenCompetenceTitle = (title: string) => {
        const start = title.indexOf("(");
        const end = title.indexOf(")");

        if (start !== -1 && end !== -1) {
            return title.substring(start + 1, end);
        } else {
            return title;
        }
    };

    getLongCompetencesSumUpPerDay = (input_data: CompetenceSumUpInput): CompetenceSumUp => {
        const all_needed_competences_by_shift: ShiftCompetencesMap = new Map();
        const all_needed_competences_by_day: ShiftCompetencesMap = new Map();

        for (const shift of input_data.shifts) {
            const line_external_ids = shift.line_external_ids;
            const needed_competences = this.getCompetences(line_external_ids);
            all_needed_competences_by_shift.set(shift.shift_tag, needed_competences);

            // Extract the date (YYYY-MM-DD) from the shift tag
            const isoDate = isoDateFromShiftTag(shift.shift_tag);

            // Retrieve or initialize the daily competences map
            let day_competences = all_needed_competences_by_day.get(isoDate);
            if (!day_competences) {
                day_competences = new Map();
                all_needed_competences_by_day.set(isoDate, day_competences);
            }

            // Sum up competences for the day
            for (const [competence, needed_value] of needed_competences) {
                const current_value = day_competences.get(competence) || 0;
                day_competences.set(competence, current_value + needed_value);
            }
        }

        return {
            all_needed_competences_by_day,
            all_needed_competences_by_shift,
        };
    };

    getDirtyShifts = (new_state?: boolean): IShiftLineRec[] => {
        const { shifts, shifts_map } = this.state;
        const dirty_shifts: IShiftLineRec[] = [];
        for (const shift of shifts) {
            const orig_shift = shifts_map.get(getShiftKey(shift));
            if (
                orig_shift !== undefined &&
                orig_shift.enabled !== shift.enabled &&
                (new_state === undefined || shift.enabled === new_state)
            ) {
                dirty_shifts.push(shift);
            }
        }

        return dirty_shifts;
    };

    haveShiftsChanged = (): boolean => {
        const { shifts, shifts_map } = this.state;
        for (const shift of shifts) {
            const orig_shift = shifts_map.get(getShiftKey(shift));
            if (orig_shift === undefined || orig_shift.enabled !== shift.enabled) {
                return true;
            }
        }

        return false;
    };

    hasPreplanningDataChanged = (): boolean => {
        // TODO: Implement
        return false;
    };

    shiftChanged = (shift: IShiftLineRec): boolean => {
        const { shifts_map } = this.state;
        const orig_shift = shifts_map.get(getShiftKey(shift));
        return orig_shift !== undefined && orig_shift.enabled !== shift.enabled;
    };

    handleDateChange = (date: Date) => {
        this.setState({ date }, () => {
            this.loadData(true);
        });
    };

    handleShiftToggle = (shift: IShiftLineRec) => {
        shift.enabled = !shift.enabled;
        const shifts = this.state.shifts;
        this.setState({ shifts });
    };

    handleSave = async () => {
        // Get disabled shifts for production lines
        const disabled_shifts = this.getDirtyShifts(false);
        if (disabled_shifts.length === 0) {
            await this.save();
            return;
        }

        // Get people schedule on the range of disabled shifts
        const schedule = await this.getPeopleScheduleForShifts(disabled_shifts);
        if (schedule === null) {
            await this.save();
            return;
        }

        const { lines, hash } = schedule;
        lines.sort((a, b) => a.line_title.localeCompare(b.line_title));

        // Map production lines to their disabled shifts
        const line_uuid_to_disabled_shift_tags_map: Map<string, Set<string>> = new Map();
        for (const shift of disabled_shifts) {
            const line_uuid = shift.line_uuid;
            let disabled_shift_tags = line_uuid_to_disabled_shift_tags_map.get(line_uuid);
            if (disabled_shift_tags === undefined) {
                disabled_shift_tags = new Set();
                line_uuid_to_disabled_shift_tags_map.set(line_uuid, disabled_shift_tags);
            }

            disabled_shift_tags.add(shiftTag(shift.year, shift.week, shift.shift_number));
        }

        const child_line_uuid_to_parent_line_map = mapChildLinesToParentLine(this.state.lines, this.state.child_lines);

        // People schedule by lines, containing only sifts that were disabled and have some people assigned
        const dirty_people_schedule: IShiftPersonLineRec[] = [];

        // People schedule updates, containing all the shifts in range
        const people_schedule_updates: IShiftPersonLineStoreRec[] = [];

        for (const line of lines) {
            const line_uuid = line.line_uuid;

            // Check parent line if it has any disabled shifts. If line is a parent line, use its own UUID, since
            // techincally it is possible to have people scheduled directly on production lines.
            const parent_line = child_line_uuid_to_parent_line_map.get(line_uuid);
            const disabled_shift_tags = line_uuid_to_disabled_shift_tags_map.get(parent_line?.uuid ?? line_uuid);
            if (disabled_shift_tags === undefined) {
                continue;
            }

            const dirty_people_schedule_line: IShiftPersonLineRec = {
                ...line,
                shifts: [],
            };
            const people_schedule_updates_line: IShiftPersonLineStoreRec = {
                line_uuid,
                shifts: [],
            };
            for (const shift of line.shifts) {
                people_schedule_updates_line.shifts.push({
                    shift_tag: shift.shift_tag,
                    person_uuids: disabled_shift_tags.has(shift.shift_tag)
                        ? []
                        : shift.persons.map(person => person.uuid),
                });

                if (disabled_shift_tags.has(shift.shift_tag) && shift.persons.length > 0) {
                    dirty_people_schedule_line.shifts.push(shift);
                }
            }

            people_schedule_updates.push(people_schedule_updates_line);
            if (dirty_people_schedule_line.shifts.length > 0) {
                dirty_people_schedule.push(dirty_people_schedule_line);
            }
        }

        // If no people are to be removed, skip confirmation
        if (dirty_people_schedule.length === 0) {
            await this.save();
            return;
        }

        // Show confirmation modal
        this.setState({
            show_save_confirmation_modal: true,
            dirty_people_schedule,
            people_schedule_updates,
            people_schedule_hash: hash,
        });
    };

    handleSaveConfirmationAccept = () => {
        this.setState({
            show_save_confirmation_modal: false,
        });
        this.save();
    };

    handleSaveConfirmationCancel = () => {
        this.setState({
            show_save_confirmation_modal: false,
            dirty_people_schedule: [],
            people_schedule_updates: [],
            people_schedule_hash: "",
        });
    };

    getPeopleScheduleForShifts = async (shifts: IShiftLineRec[]): Promise<IGetPeopleShiftsTableRes | null> => {
        if (shifts.length === 0) {
            return null;
        }

        // Get production lines from given shifts
        const production_line_uuids = new Set(shifts.map(shift => shift.line_uuid));

        // Get shift table for all production lines and their child lines
        // Start with production lines
        const shift_table_line_uuids = [...production_line_uuids];

        // Get child lines for production lines
        const child_line_uuid_to_parent_line_map = mapChildLinesToParentLine(this.state.lines, this.state.child_lines);
        for (const [child_line_uuid, parent_line] of child_line_uuid_to_parent_line_map.entries()) {
            if (production_line_uuids.has(parent_line.uuid)) {
                shift_table_line_uuids.push(child_line_uuid);
            }
        }

        const shift_bounds = getShiftBounds(shifts.map(shift => shiftTag(shift.year, shift.week, shift.shift_number)));
        if (shift_bounds === null) {
            return null;
        }

        // Get people schedule for affected range of shifts on selected lines
        try {
            const [from_shift_number, to_shift_number] = shift_bounds;
            const people_shifts_table = await getBackend().manufacturing.getPeopleShiftsTable({
                line_uuids: shift_table_line_uuids,
                from_shift: from_shift_number.shift_tag,
                to_shift: to_shift_number.shift_tag,
                include_tool_changes: false,
                include_work_orders: false,
            });

            return people_shifts_table;
        } catch (error: unknown) {
            this.setState({
                error: translate("common.error", "An error occurred while fetching data"),
            });
            console.error(error);
        }

        return null;
    };

    save = async () => {
        this.setState({ is_saving: true });
        try {
            // Save preplanning data
            const { expected_productions, preplanning_lines, shifts, shifts_map, people_schedule_hash } = this.state;
            const { line_group_uuid } = this.props;
            const { date } = this.state;
            await getBackend().manufacturing.updatePreplanningData({
                line_group_uuid,
                month: date.getMonth(),
                year: date.getFullYear(),
                expected_productions,
                lines: preplanning_lines,
            });

            // Save shifts
            const lines_map: Map<string, IUpdateLineShifts> = new Map();
            for (const shift of shifts) {
                const orig_shift = shifts_map.get(getShiftKey(shift));
                if (orig_shift === undefined || orig_shift.enabled === shift.enabled) {
                    continue;
                }

                const { line_uuid } = shift;
                let line = lines_map.get(line_uuid);
                if (line === undefined) {
                    line = { line_uuid, shifts: [] };
                    lines_map.set(line_uuid, line);
                }

                line.shifts.push({
                    shift_uuid: shift.shift_uuid,
                    enabled: shift.enabled,
                });
            }

            if (lines_map.size > 0) {
                await getBackend().manufacturing.updateLineShiftsEx({
                    lines: [...lines_map.values()],
                });

                // Remove people assigned to closed shifts
                const { people_schedule_updates } = this.state;
                const shifts = people_schedule_updates[0]?.shifts ?? [];
                const shift_bounds = getShiftBounds(shifts.map(shift => shift.shift_tag));
                if (shift_bounds !== null) {
                    const [first_shift_number, last_shift_number] = shift_bounds;
                    await getBackend().manufacturing.storePeopleShiftAssignments({
                        first_shift_ts: first_shift_number.ts_start,
                        last_shift_ts: last_shift_number.ts_end,
                        hash: people_schedule_hash,
                        lines: people_schedule_updates,
                    });
                    this.setState({
                        dirty_people_schedule: [],
                        people_schedule_updates: [],
                        people_schedule_hash: "",
                    });
                }

                await this.loadData(true, false);
            }
        } catch (error: unknown) {
            this.setState({
                error: translate("common.error_save", "An error occurred while saving data"),
            });
            console.error(error);
        }

        this.setState({ is_saving: false });
    };

    updateShiftData = (shift: IPreplanningOverrideDataLine, line_external_id: string) => {
        const preplanning_lines = this.state.preplanning_lines;
        const line = preplanning_lines.find(line => line.line_external_id === line_external_id);
        if (line !== undefined) {
            const overrides = line.overrides || [];
            const shift_override = overrides.find(override => override.shift_tag === shift.shift_tag);

            if (shift.comment === "" && shift.quantity === 0 && shift.type === SHIFT_MODIFIERS.none) {
                line.overrides = overrides.filter(override => override.shift_tag !== shift.shift_tag); // remove shift override
            } else {
                if (shift_override !== undefined) {
                    shift_override.quantity = shift.quantity;
                    shift_override.comment = shift.comment;
                    shift_override.type = shift.type;
                } else {
                    overrides.push(shift);
                }
                if (line.overrides === undefined) {
                    line.overrides = overrides;
                }
            }
        }

        this.setState({ preplanning_lines });
    };

    handleCalculate = async () => {
        this.setState({ is_calculating: true, error: "" });

        const { shifts, lines, preplanning_lines } = this.state;
        const response = await getBackend().manufacturing.calculatePreplanningData({
            shifts,
            lines,
            preplanning_lines,
        });

        const response_lines: IPreplanningDataLine[] = response.lines;

        this.setState(
            {
                is_calculating: false,
                preplanning_lines: response_lines,
            },
            () =>
                this.calculateNewSums(
                    shifts,
                    lines,
                    response_lines,
                    this.state.line_product_group,
                    this.state.product_group_line_map
                )
        );
    };

    handlePreplanningChartToggle = () => {
        this.setState(prev_state => ({
            ...prev_state,
            show_preplanning_chart_modal: !prev_state.show_preplanning_chart_modal,
        }));
    };

    openEditLineModal = (line_uuid: string) => {
        this.setState({ edit_line_modal_show: true, editing_line_uuid: line_uuid });
    };

    closeEditLineModal = (shift_template: CalendarLineOption | null) => {
        if (shift_template != null && shift_template.value != null) {
            const shift_value = shift_template.value;
            const today = new Date();
            const shift_tag_now = shiftNumber(today).shift_tag;

            const newShifts = this.state.shifts.map(shift => {
                if (shift.line_uuid === this.state.editing_line_uuid) {
                    const shift_tag = shiftTag(shift.year, shift.week, shift.shift_number);

                    if (shift_tag_now < shift_tag) {
                        // Determine if the shift should be enabled based on the shift value
                        const should_enable = shift_value.charAt(shift.shift_number % shift_value.length) === "1";

                        // Return a new shift object with the 'enabled' property set accordingly
                        return { ...shift, enabled: should_enable };
                    }
                }
                return shift;
            });

            this.setState({ shifts: newShifts });
        }
        this.setState({ edit_line_modal_show: false, editing_line_uuid: "", calendar_line: "" });
    };

    renderSaveCalendarLineModal() {
        return (
            <PreplanningEditLineModal
                show={this.state.edit_line_modal_show}
                onHide={this.closeEditLineModal}
                calendar_line_options={this.state.calendar_line_options}
            />
        );
    }

    handleLineParametersChange = (line: ILineData) => {
        const line_index = this.state.lines.findIndex(l => l.uuid === line.uuid);
        if (line_index !== -1) {
            const lines = [...this.state.lines];
            lines[line_index] = line;
            this.setState({ lines });
        }
    };

    render() {
        const {
            child_lines,
            date,
            error,
            expected_productions,
            is_calculating,
            is_loading,
            is_saving,
            line_product_group,
            lines,
            preplanning_lines,
            // production_status_period,
            shifts,
        } = this.state;

        const is_toolbar_disabled = is_loading || is_saving || is_calculating;

        return (
            <article className="preplanning article">
                <section id="statistics" className="data_sources h-100 pb-0 mt-n3">
                    <div className="preplanning__toolbar filter_bar views-filter-bar">
                        <div className="row align-items-center">
                            <div className="col col-auto ml-auto">
                                <div className="form-row">
                                    {/* <span className="row align-items-center m-0">
                                        {production_status_period ? <FormattedMessage id="common.total" defaultMessage="Total" />
                                            : <FormattedMessage id="Preplanning.until_today" defaultMessage="Until today" />}
                                    </span>
                                    <div className="col-auto">
                                        <Toggle name="production_status_period" defaultChecked={production_status_period}
                                            className="vertical-align-middle"
                                            onChange={() => this.setState({ production_status_period: !production_status_period })} />
                                    </div> */}
                                    <div className="col-auto">
                                        <MonthPicker
                                            date={date}
                                            disabled={is_toolbar_disabled}
                                            onChange={this.handleDateChange}
                                        />
                                    </div>
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-short btn-outline-secondary"
                                            disabled={is_toolbar_disabled}
                                            onClick={this.handlePreplanningChartToggle}
                                        >
                                            <i className="fas fa-chart-line" />
                                        </button>
                                    </div>
                                    <div className="col-auto">
                                        <button
                                            className="btn btn-outline-primary"
                                            disabled={is_toolbar_disabled}
                                            onClick={() => this.setState({ production_group_sum_modal_show: true })}
                                        >
                                            <i className="fas fa-balance-scale mr-1" />{" "}
                                            {translate("common.total_sum", "Total sum")}{" "}
                                        </button>
                                    </div>
                                    {Auth.roleCanEditPreplanning() && (
                                        <div className="col-auto">
                                            <button
                                                className="btn btn-outline-primary"
                                                disabled={is_toolbar_disabled}
                                                onClick={this.handleCalculate}
                                            >
                                                <i className="fas fa-calculator mr-1" />{" "}
                                                {translate("Manufacturing.Planning.calculate", "Calculate")}{" "}
                                                {is_calculating && <i className="fas fa-spinner fa-spin ml-1" />}
                                            </button>
                                        </div>
                                    )}
                                    {Auth.roleCanEditPreplanning() && (
                                        <div className="col-auto">
                                            <button
                                                className="btn btn-primary"
                                                disabled={is_toolbar_disabled}
                                                onClick={this.handleSave}
                                            >
                                                <i className="fas fa-save mr-1" /> {translate("common.save", "Save")}{" "}
                                                {is_saving && <i className="fas fa-spinner fa-spin ml-1" />}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="preplanning__container container-fluid">
                        {is_loading ? (
                            <Loader />
                        ) : (
                            <div className="preplanning__table-wrapper">
                                {error !== "" && <ErrorComponent className="m-3" type="error" msg={error} />}
                                <PreplanningTable
                                    line_group_uuid={this.props.line_group_uuid}
                                    lines={lines}
                                    child_lines={child_lines}
                                    preplanning_lines={preplanning_lines}
                                    shifts={shifts}
                                    shiftChanged={this.shiftChanged}
                                    onShiftToggle={this.handleShiftToggle}
                                    openEditLineModal={this.openEditLineModal}
                                    getCompetences={this.getCompetences}
                                    getCompetencesSumUp={this.getCompetencesSumUpPerDay}
                                    lineGroupMap={this.state.line_product_group}
                                    productGroupWithLines={this.state.product_group_line_map}
                                    updateShiftData={this.updateShiftData}
                                    map_available_people_per_shift={this.state.map_available_people_per_shift}
                                    map_available_people_per_day={this.state.map_available_people_per_day}
                                    product_group_sum_map={this.state.product_group_sum_map}
                                    sum_per_line={this.state.sum_per_line}
                                    production_status_period={this.state.production_status_period}
                                    public_holidays={this.state.public_holidays}
                                    expected_productions={expected_productions}
                                    onLineParametersChange={this.handleLineParametersChange}
                                />
                            </div>
                        )}
                    </div>
                    {this.renderSaveCalendarLineModal()}
                    <PreplanningProductGroupSumModal
                        show={this.state.production_group_sum_modal_show}
                        onHide={() => this.setState({ production_group_sum_modal_show: false })}
                        product_group_sum_map={this.state.product_group_sum_map}
                        expected_productions={this.state.expected_productions}
                        date={this.state.date}
                    />
                    <PreplanningChartModal
                        date={date}
                        show={this.state.show_preplanning_chart_modal}
                        expected_productions={expected_productions}
                        lines={lines}
                        line_material_group_map={line_product_group}
                        preplanning_lines={preplanning_lines}
                        linegroup_uuid={this.props.line_group_uuid}
                        onClose={this.handlePreplanningChartToggle}
                    />
                    <SaveConfirmationModal
                        show={this.state.show_save_confirmation_modal}
                        onAccept={this.handleSaveConfirmationAccept}
                        onCancel={this.handleSaveConfirmationCancel}
                        schedule={this.state.dirty_people_schedule}
                    />
                </section>
            </article>
        );
    }
}

export default Preplanning;
